import { useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { useAppSelector, useAppDispatch } from '@state/hooks'
import { setActiveDocument, setDocumentContent, setDocumentError, DocType, RelatedDoc } from '@state/slices/documentSlice'
import { omapApi } from '@services/omapApi'
import { featuredDocFor } from '@utils/featuredDocs'
import FeaturedDocCard from '@components/tabs/review/FeaturedDocCard'
import './DocumentTab.css'

function DocumentTab() {
  const dispatch = useAppDispatch()
  const { activeNodeId, activeDocType, content, name, loading, error, relatedDocs } = useAppSelector(
    (state) => state.document
  )
  const { activeCompanyId } = useAppSelector((state) => state.companies)
  const companyId = activeCompanyId
    ? parseInt(activeCompanyId.replace('company_', ''), 10)
    : null

  useEffect(() => {
    if (!activeNodeId || !activeDocType) return

    let cancelled = false

    async function fetchDocument() {
      try {
        const data = await omapApi.getDocument(activeDocType!, activeNodeId!)
        if (cancelled) return
        dispatch(
          setDocumentContent({
            content: data.content || data.summary || data.description || null,
            name: data.name || activeNodeId!,
            relatedDocs: data.related_docs || [],
          })
        )
        if (data.error) {
          dispatch(setDocumentError(data.error))
        }
      } catch (err: any) {
        if (cancelled) return
        const msg = err?.response?.data?.detail || err?.message || 'Failed to load document'
        dispatch(setDocumentError(msg))
      }
    }

    fetchDocument()
    return () => {
      cancelled = true
    }
  }, [activeNodeId, activeDocType, dispatch])

  function handleRelatedDocClick(doc: RelatedDoc) {
    dispatch(setActiveDocument({ nodeId: doc.id, docType: doc.doc_type as DocType }))
  }

  // Empty state: featured document landing card
  if (!activeNodeId) {
    const featured = featuredDocFor(companyId)
    if (featured) {
      return (
        <div className="document-tab document-tab--featured">
          <FeaturedDocCard
            doc={featured}
            onOpen={(doc) =>
              dispatch(setActiveDocument({ nodeId: doc.nodeId, docType: doc.docType }))
            }
          />
        </div>
      )
    }
    return (
      <div className="document-tab">
        <div className="document-empty-state">
          <div className="document-empty-icon">📋</div>
          <h3>Document Viewer</h3>
          <p>
            Select a document link from the <strong>Analysis Report</strong> to view its contents here.
          </p>
          <p className="document-empty-hint">
            Process documentation, competency frameworks (KSA), and technology requirements (DCA)
            can be viewed in full from this tab.
          </p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="document-tab">
        <div className="document-loading">
          <div className="document-spinner" />
          <p>Loading document...</p>
        </div>
      </div>
    )
  }

  // Content display
  const docTypeLabel =
    activeDocType === 'process' ? 'Process Document' :
    activeDocType === 'ksa' ? 'KSA Competency Document' :
    activeDocType === 'dca' ? 'DCA Technology Document' :
    'Document'

  const ksaDocs = relatedDocs.filter(d => d.doc_type === 'ksa')
  const dcaDocs = relatedDocs.filter(d => d.doc_type === 'dca')
  const hasRelatedDocs = ksaDocs.length > 0 || dcaDocs.length > 0

  return (
    <div className="document-tab">
      <div className="document-header">
        <span className="document-type-badge">{docTypeLabel}</span>
        <h2 className="document-title">{name || activeNodeId}</h2>
      </div>

      {error && (
        <div className="document-error">
          {error}
        </div>
      )}

      {content ? (
        <div className="document-content document-markdown">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      ) : !error ? (
        <div className="document-no-content">
          <p>No source file content available for this document.</p>
          <p className="document-node-id">Node ID: {activeNodeId}</p>
        </div>
      ) : null}

      {hasRelatedDocs && (
        <div className="document-related-section">
          <h3 className="document-related-heading">Related Documents</h3>
          {ksaDocs.length > 0 && (
            <div className="document-related-group">
              <h4 className="document-related-group-label">Competency Frameworks (KSA)</h4>
              {ksaDocs.map(doc => (
                <button
                  key={doc.id}
                  className="document-related-link document-related-ksa"
                  onClick={() => handleRelatedDocClick(doc)}
                >
                  <span className="document-related-icon">🎓</span>
                  <span className="document-related-name">{doc.name}</span>
                  <span className="document-related-desc">Required knowledge, skills, and abilities</span>
                </button>
              ))}
            </div>
          )}
          {dcaDocs.length > 0 && (
            <div className="document-related-group">
              <h4 className="document-related-group-label">Technology Requirements (DCA)</h4>
              {dcaDocs.map(doc => (
                <button
                  key={doc.id}
                  className="document-related-link document-related-dca"
                  onClick={() => handleRelatedDocClick(doc)}
                >
                  <span className="document-related-icon">💻</span>
                  <span className="document-related-name">{doc.name}</span>
                  <span className="document-related-desc">Required data, capabilities, and attributes</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DocumentTab
